/**
 * Normalizes LinkedIn post URLs by removing standard tracking query parameters
 * before the URL is persisted or sent to backend APIs.
 */

export const TRACKING_QUERY_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
];

/**
 * Strips UTM and tracking query parameters from a LinkedIn post URL.
 * Preserves the protocol, hostname, path, post identifier, and any non-tracking parameters.
 * If the input cannot be parsed by URL API, returns the trimmed original string so existing
 * validation handlers can report errors appropriately.
 *
 * @param {string} rawUrl - The raw input URL
 * @returns {string} - The cleaned URL without tracking parameters
 */
export const normalizeLinkedInUrl = (rawUrl) => {
  if (!rawUrl || typeof rawUrl !== "string") {
    return rawUrl;
  }

  const trimmed = rawUrl.trim();

  try {
    const urlObj = new URL(trimmed);

    // Remove standard tracking parameters
    TRACKING_QUERY_PARAMS.forEach((param) => {
      urlObj.searchParams.delete(param);
    });

    // Remove any additional parameter starting with 'utm_' (case-insensitive)
    const keysToDelete = [];
    urlObj.searchParams.forEach((_, key) => {
      if (key.toLowerCase().startsWith("utm_")) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach((key) => urlObj.searchParams.delete(key));

    let cleanUrl = urlObj.toString();
    // Strip trailing '?' if all query parameters were removed
    if (cleanUrl.endsWith("?")) {
      cleanUrl = cleanUrl.slice(0, -1);
    }

    return cleanUrl;
  } catch {
    // If not a valid parseable URL, return trimmed string to let existing regex validate it
    return trimmed;
  }
};
